import React, { MouseEventHandler } from 'react';
import { Div, DivProps, P } from '@kizi/core-components';
import { IProduct } from '@kizi/comparator-core';

function formatPrice(price: string | number | null, decimals = 2): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(price) || 0);
}

export interface PriceProps extends DivProps {
  data?: IProduct;
  decimals?: number;
  hideIfZero?: boolean;
  onLabelClick?: MouseEventHandler;
  value: number;
  pricePostfix?: string;
  labelProps?: any;
  variant?: string;
  hidePriceSymbol?: boolean;
}

const PriceV2 = ({
  price,
  hidePriceSymbol = false,
  centsProps = {},
  mainPriceProps = {},
  ...rest
}) => {
  // Regular expression to capture the symbol, main price, and cents, handling thousands separators
  const priceRegex = /([^\d\s]+)?\s*([\d.]+)[,](\d+)/;
  const match = price.match(priceRegex);

  if (!match) {
    console.error('Invalid price format:', price);
    return null;
  }

  const [, symbol = '€', rawMainPrice, cents] = match;

  // Replace dots in rawMainPrice for parsing and format it with localized thousands separators
  const mainPriceNumber = parseInt(rawMainPrice.replace(/\./g, ''), 10);
  const formattedMainPrice = new Intl.NumberFormat('de-DE').format(
    mainPriceNumber,
  );

  return (
    <Div display={'flex'} justifyContent={'flex-end'} {...rest}>
      <P
        variant="baseBodyImportant"
        fontSize={'40px'}
        lineHeight={'92%'}
        {...mainPriceProps}
      >
        {!hidePriceSymbol && symbol}&nbsp;
        {formattedMainPrice}
      </P>
      <P
        variant="baseBodyImportant"
        lineHeight={'unset'}
        alignSelf={'start'}
        {...centsProps}
      >
        ,{cents}
      </P>
    </Div>
  );
};

export const Price: React.FC<PriceProps> = (props: PriceProps) => {
  const {
    decimals = 2,
    hideIfZero,
    onLabelClick,
    value,
    pricePostfix = '',
    labelProps,
    hidePriceSymbol,
    ...restProps
  } = props;

  const _value = (value || 0).toFixed(decimals);

  if (value === 0 && hideIfZero) return null;

  return (
    <Div {...restProps}>
      <Div>
        <Div display="flex" gridGap="8px">
          <Div>
            <PriceV2
              price={formatPrice(_value)}
              mainPriceProps={{ fontSize: '40px' }}
              centsProps={{ fontSize: 5 }}
              hidePriceSymbol={hidePriceSymbol}
            />

            <P
              variant="regularV2"
              color={'main.5'}
              textAlign={'end'}
              lineHeight={'unset'}
            >
              per maand
            </P>
          </Div>
        </Div>
      </Div>
    </Div>
  );
};
